import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  makeStyles,
  Typography,
  FormHelperText,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useLoading } from "../../../../util/LoadingProvider";
import { useHistory } from "react-router";
import apiHelper from "../../../../util/ApiHelper";
import styled from "styled-components";
import { modal } from "../../../../util";
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';

const schema = yup.object().shape({
  localizations: yup.object().shape({
    en: yup.object().shape({
      name: yup.string(),
    }),
    tc: yup.object().shape({
      name: yup.string(),
    }),
  }),
  perItemPrice: yup.number(),
  perOrderPrice: yup.number(),
  categoryId: yup.string().required("Category cannot be empty"),
  status: yup.string(),
});

const defaultFormValues = {
  id: 0,
  seq: 0,
  categoryId: "",
  countryCode: "",
  perOrderPrice: null,
  perItemPrice: null,
  localizations: {
    en: {
      name: "",
      perOrderPriceDescription: "",
      perItemPriceDescription: ""
    },
    tc: {
      name: "",
      perOrderPriceDescription: "",
      perItemPriceDescription: ""
    }
  },
  status: "ACTIVE",
};

const useStyles = makeStyles((theme) => ({
  textInput: {
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "362px",
    },
  },
  buttonGroup: {
    marginTop: "40px",
    textAlign: "left",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  input: {
    width: "100%",
  },
}));

const PriceTag = styled.span`
  color: #CCCCCC;
  transform: scale(0.8);
`;

export default function ShippingEditPage({ disabled, ...props }) {
  const { match } = props;
  const history = useHistory();
  const id = match.params.id;
  const [defaultValues, setDefaultValues] = useState(defaultFormValues);
  const [shippingCatList, setShippingCatList] = useState([]);
  const { setLoading } = useLoading();

  const classes = useStyles();

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  const onSubmit = (value) => {
    console.log("value:", value);
    if (id !== "new") updateShipping(value);
    else createShipping(value);
  };

  const getShippingCategories = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getShippingCategories();
      console.log(result);
      setShippingCatList(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, reset, setLoading]);

  const getShipping = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiHelper.hkiff.getShippingById(id);
      console.log(result);
      result.data.categoryId = result.data.category.id;
      setDefaultValues(result.data);
      reset(result.data);
    } catch (err) {
      console.log(err);
    }
    setLoading(false);
  }, [id, reset, setLoading]);

  const updateShipping = async (value) => {
    try {
      const result = await apiHelper.hkiff.updateShippingById(id, value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("Your changes are saved.");
        history.push(`/hkiff/eshop/shipping-setting/${id}`);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const createShipping = async (value) => {
    try {
      console.log(value);
      const result = await apiHelper.hkiff.createShipping(value);
      console.log(result);
      if (result.status === 200) {
        toast.dark("The shipping setting is created.");
        history.push(`/hkiff/eshop/shipping-setting/${result.data.id}`);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const deleteShipping = async () => {
    const decision = await modal.fire({
      title: "Delete Shipping Region",
      text: "Are you sure to delete this Shipping Region?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.hkiff.deleteShippingById(id);
        if (result.data.code === 0) {
          toast.dark("Shipping Region deleted.");
          history.push(`/hkiff/eshop/shipping-setting`);
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    getShippingCategories();
    if (id !== "new") getShipping();
  }, [getShipping, id]);

  return (
    <div className={classes.content}>
      <Box width="100%">
        <Typography variant="h4" gutterBottom>
          eShop &gt; {id === "new" ? "Add Shipping Setting" : "Edit Shipping Setting"}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={disabled} className="fieldset">
          <Grid container xs={6} direction="row" spacing={3}>
            <Grid item xs={12}>
            {shippingCatList.length > 0 &&
              <FormControl variant="outlined" margin="dense" className={classes.formControl}
                style={{ width: "100%" }}>
                <InputLabel htmlFor="outlined-age-native-simple">Select Category</InputLabel>
                <Controller
                  name={`categoryId`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                    <Select
                      native
                      label="Select Category"
                      {...field}
                      inputProps={{
                        name: 'categoryId',
                        id: 'outlined-category-native-simple',
                      }}
                      error={!!error}
                    >
                      <option value={null}>None</option>
                      {shippingCatList.map((o, k) => {
                        return (<option key={o.id} value={o.id}>{o.localizations.en.name}</option>)
                      })}
                    </Select>
                    {error && 
                    <FormHelperText className={classes.margin}>
                      {error ? error.message : ""}
                    </FormHelperText>
                    }
                    </>
                  )}
                />
              </FormControl>
              }
            </Grid>
            <Grid item xs={12}>
              
              <Controller
                name="countryCode"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Country Code"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Region Name (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.name"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Region Name (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.perOrderPriceDescription"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="per Order Price Description (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.perOrderPriceDescription"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="per Order Price Description (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="perOrderPrice"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="per Order Price"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                    type='number'
                    InputProps={{
                      startAdornment: (
                        <PriceTag>HK$</PriceTag>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.perItemPriceDescription"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="per Item Price Description (English)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.perItemPriceDescription"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="per Item Price Description (Traditional Chinese)"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="perItemPrice"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="per Item Price"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                    type='number'
                    InputProps={{
                      startAdornment: (
                        <PriceTag>HK$</PriceTag>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            {/* not yet implemented */}
            {/* <Grid item xs={12}>
            <Controller
              name="category"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  label="Category"
                  variant="outlined"
                  margin="dense"
                  className={classes.textInput}
                  fullWidth
                  {...field}
                  error={!!error}
                  helperText={error ? error.message : ""}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              )}
            />
          </Grid> */}
          </Grid>

          <Grid
            container
            item
            xs={12}
            spacing={3}
            style={{ marginTop: "52px", marginLeft: 0 }}
          >
            <Grid item xs={3} style={{ paddingLeft: "4px" }}>
              <Controller
                control={control}
                name="status"
                render={({
                  field: { value, onChange, ...rest },
                  fieldState: { error },
                }) => (
                  <ToggleButtonGroup
                    {...rest}
                    value={value}
                    onChange={(event, newFormats) => {
                      onChange(newFormats);
                    }}
                    exclusive
                    aria-label="Status"
                    className={classes.input}
                  >
                    <ToggleButton
                      value={"ACTIVE"}
                      aria-label="Active"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Active
                    </ToggleButton>
                    <ToggleButton
                      value={"INACTIVE"}
                      aria-label="Inactive"
                      style={{ width: "50%" }}
                      disabled={disabled}
                    >
                      Inactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </Grid>
          </Grid>

          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="submit"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
              >
                Save Changes
              </Button>
              {(id != "new" && id) && (
                <Button
                  variant="text"
                  disableElevation
                  style={{
                    color: "#962013",
                    padding: "9px 16px",
                  }}
                  onClick={() => {
                    deleteShipping(id);
                  }}
                >
                  Delete
                </Button>
              )}
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
